/**
 * WelcomeScreen - FMEA Wizard Welcome & Path Selection
 * Shows 4 paths to create an FMEA: template, BOM import, from scratch, copy existing
 */

import React from 'react';
import { useWizard, type WizardPath } from '../../contexts/WizardContext';
import './WelcomeScreen.css';

interface PathOption {
  id: WizardPath;
  title: string;
  description: string;
  icon: string;
  color: string;
  estimatedTime: string;
}

const paths: PathOption[] = [
  {
    id: 'template',
    title: 'Use a Template',
    description: 'Start with a pre-built FMEA template for your industry',
    icon: '📋',
    color: '#4CAF50',
    estimatedTime: '5 minutes',
  },
  {
    id: 'bom',
    title: 'Import BOM',
    description: 'Upload your Bill of Materials to generate component structure',
    icon: '📤',
    color: '#2196F3',
    estimatedTime: '10 minutes',
  },
  {
    id: 'scratch',
    title: 'Start from Scratch',
    description: 'Build your FMEA step-by-step with guided assistance',
    icon: '✏️',
    color: '#FF9800',
    estimatedTime: '30 minutes',
  },
  {
    id: 'copy',
    title: 'Copy Existing FMEA',
    description: 'Duplicate a previous FMEA as a starting point',
    icon: '📑',
    color: '#9C27B0',
    estimatedTime: '5 minutes',
  },
];

export function WelcomeScreen() {
  const { selectPath, cancelWizard } = useWizard();

  const handleSelectPath = (pathId: WizardPath) => {
    selectPath(pathId);
  };

  const handleSkip = () => {
    // Set localStorage flag to not show wizard again
    localStorage.setItem('fmeastudio_wizard_dismissed', 'true');
    cancelWizard();
  };

  return (
    <div className="welcome-screen" data-testid="wizard-welcome-screen">
      <div className="welcome-content">
        <h1 className="welcome-title">Welcome to FMEAstudio</h1>
        <p className="welcome-subtitle">How would you like to create your FMEA?</p>

        <div className="path-grid">
          {paths.map((path) => (
            <button
              key={path.id}
              className="path-card"
              onClick={() => handleSelectPath(path.id)}
              data-testid={`wizard-path-${path.id}`}
              style={{ '--path-color': path.color } as React.CSSProperties}
            >
              <div className="path-icon" style={{ backgroundColor: path.color }}>
                {path.icon}
              </div>
              <div className="path-info">
                <h3 className="path-title">{path.title}</h3>
                <p className="path-time">Est. {path.estimatedTime}</p>
              </div>
              <p className="path-description">{path.description}</p>
            </button>
          ))}
        </div>

        <div className="welcome-actions">
          <button className="skip-button" onClick={handleSkip} data-testid="wizard-skip-button">
            Skip wizard, take me to the app
          </button>
        </div>
      </div>
    </div>
  );
}
